import {
  Add,
  ArrowDropDown,
  Close,
  Done,
  FilterAltOutlined,
  Search,
} from "@mui/icons-material";
import {
  alpha,
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Divider,
  IconButton,
  Paper,
  Popover,
  Popper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState, useRef } from "react";

interface FilterGenericProps {
  items?: string[];
  selectedItems?: string[];
  setSelectedItems?: (v: string[] | ((prev: string[]) => string[])) => void;
  visibleItems?: string[];
  label?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  filters?: Record<string, string[]>;
  selectedFilters?: Record<string, string[]>;
  setSelectedFilters?: (
    v:
      | Record<string, string[]>
      | ((prev: Record<string, string[]>) => Record<string, string[]>),
  ) => void;
}

export default function FilterGeneric({
  items = [],
  selectedItems = [],
  setSelectedItems = () => {},
  visibleItems,
  label = "Filtros",
  searchPlaceholder = "Buscar",
  emptyText = "Nenhum resultado encontrado",
  filters = {},
  selectedFilters = {},
  setSelectedFilters = () => {},
}: FilterGenericProps) {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [moreFiltersAnchorEl, setMoreFiltersAnchorEl] =
    useState<HTMLElement | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const isDownMd = useMediaQuery(theme.breakpoints.down("lg"));
  const numberExibitionTags = isDownMd ? 1 : 3;

  const openMoreFilters = Boolean(moreFiltersAnchorEl);
  const totalSelectedFilters = Object.values(selectedFilters).reduce(
    (acc, arr) => acc + arr.length,
    0,
  );

  const pool = visibleItems !== undefined ? visibleItems : items;
  const filtered = pool.filter((item) =>
    item.toLowerCase().includes(search.toLowerCase()),
  );

  const styles = {
    container: {
      position: "absolute",
      display: "flex",
      flexDirection: "row" as const,
      left: { xs: "60px", md: "70px" },
      transition: "left 0.3s ease",
      top: "15px",
      alignItems: "center",
      gap: 1,
      justifyContent: "center",
      zIndex: 10,
      fontWeight: "700",
    },
    buttonFilter: {
      textTransform: "capitalize" as const,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      border: "1px solid " + theme.palette.divider,
    },
    tagPaper: {
      border: "1px solid " + theme.palette.divider,
    },
    tagButton: {
      textTransform: "capitalize" as const,
      color: theme.palette.text.primary,
    },
    popper: {
      zIndex: theme.zIndex.modal,
      width: 250,
    },
    popperContent: {
      border: "1px solid " + theme.palette.divider,
      boxShadow: "0px 0px 3px #0000001a",
      backgroundColor: theme.palette.background.paper,
      borderRadius: 1,
      marginTop: "8px",
      maxHeight: 400,
      overflow: "auto",
    },
    menuHeader: {
      width: "100%",
      fontSize: "14px",
      justifyContent: "space-between",
      alignItems: "center",
      pl: 2,
      pr: 1,
      mt: 2,
    },
    menuClearButton: {
      textTransform: "none" as const,
      fontSize: "0.85rem",
    },
    menuItem: (selected: boolean) => ({
      background: selected ? alpha(theme.palette.primary.main, 0.1) : "none",
      borderRadius: 1,
      margin: "2px 8px",
      "&:hover": {
        backgroundColor: selected
          ? alpha(theme.palette.primary.main, 0.2)
          : alpha(theme.palette.action.hover, 0.04),
      },
    }),
    textField: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        backgroundColor: (theme.palette as any).background.paperLinkedMaps,
        boxShadow: "none",
        "&:hover": {
          backgroundColor: (theme.palette as any).background.paperLinkedMaps,
        },
      },
    },
  };

  const handleToggle = () => setOpen((prev) => !prev);

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  const handleToggleMoreFilters = (e: React.MouseEvent<HTMLElement>) => {
    setMoreFiltersAnchorEl((prev) => (prev ? null : e.currentTarget));
  };

  const handleCategoryChange = (category: string, newValues: string[]) => {
    setSelectedFilters((prev) => ({ ...prev, [category]: newValues }));
  };

  const addFilter = (item: string) => {
    setSelectedItems((prev) => [...prev, item]);
  };

  const removeFilter = (item: string) => {
    setSelectedItems((prev) => prev.filter((i) => i !== item));
  };

  const MenuItem = ({ item }: { item: string }) => {
    const selected = selectedItems.includes(item);
    return (
      <Box
        onClick={() => (selected ? removeFilter(item) : addFilter(item))}
        sx={styles.menuItem(selected)}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
          px={2}
          py={1}
          sx={{ cursor: "pointer" }}
        >
          <Typography
            sx={{
              color: selected ? theme.palette.primary.main : "inherit",
              textTransform: "capitalize",
              fontSize: "0.875rem",
            }}
          >
            {item}
          </Typography>
          {selected && <Done color="primary" fontSize="small" />}
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <Stack sx={styles.container}>
        <Paper elevation={0} ref={anchorRef}>
          <Button
            startIcon={<FilterAltOutlined />}
            endIcon={<ArrowDropDown />}
            onClick={handleToggle}
            sx={styles.buttonFilter}
          >
            {label}
          </Button>
        </Paper>
        {Object.keys(filters).length > 0 && (
          <IconButton
            onClick={handleToggleMoreFilters}
            sx={{
              ...styles.buttonFilter,
              ...(openMoreFilters && {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: theme.palette.primary.main,
              }),
            }}
          >
            <Badge
              badgeContent={totalSelectedFilters}
              color="primary"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.65rem",
                  height: 16,
                  minWidth: 16,
                  top: 27,
                  right: -2,
                },
              }}
            >
              <Box
                sx={{
                  color: openMoreFilters
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  px: 0.2,
                }}
              >
                <FilterAltOutlined
                  sx={{ width: "1.35rem", height: "1.3rem", mt: 0.5, mr: 0.3 }}
                />
                <Add
                  sx={{
                    position: "absolute",
                    top: -4,
                    right: -4,
                    width: "1rem",
                    height: "1rem",
                  }}
                />
              </Box>
            </Badge>
          </IconButton>
        )}

        {selectedItems.slice(0, numberExibitionTags).map((item) => (
          <Tooltip key={item} title={item} arrow>
            <Paper elevation={0} sx={styles.tagPaper}>
              <Button
                endIcon={<Close />}
                onClick={() => removeFilter(item)}
                sx={styles.tagButton}
              >
                <Typography
                  sx={{
                    maxWidth: { xs: "50px", md: "100px" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item}
                </Typography>
              </Button>
            </Paper>
          </Tooltip>
        ))}

        {selectedItems.length > numberExibitionTags && (
          <Tooltip
            title={
              <div>
                {selectedItems.slice(numberExibitionTags).map((item, index) => (
                  <div key={item}>
                    {item}
                    {index < selectedItems.length - numberExibitionTags - 1 && (
                      <br />
                    )}
                  </div>
                ))}
              </div>
            }
            arrow
          >
            <Paper elevation={0} sx={styles.tagPaper}>
              <Button sx={styles.tagButton}>
                +{selectedItems.length - numberExibitionTags}
              </Button>
            </Paper>
          </Tooltip>
        )}
      </Stack>

      {/* Main filter */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={styles.popper}
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box sx={styles.popperContent}>
            <Stack direction="row" sx={styles.menuHeader}>
              <Typography>Adicionar Filtro</Typography>
              <Button
                variant="text"
                size="small"
                sx={styles.menuClearButton}
                onClick={() => setSelectedItems([])}
              >
                Limpar
              </Button>
            </Stack>

            <Box p={1.5}>
              <TextField
                placeholder={searchPlaceholder}
                autoComplete="off"
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={styles.textField}
                slotProps={{
                  input: {
                    endAdornment: (
                      <Search sx={{ color: theme.palette.action.disabled }} />
                    ),
                  },
                }}
                autoFocus
              />
            </Box>

            <Box sx={{ maxHeight: 250, overflow: "auto", pb: 1 }}>
              {filtered
                .slice()
                .sort((a, b) => a.localeCompare(b))
                .map((item) => (
                  <MenuItem key={item} item={item} />
                ))}
              {filtered.length === 0 && (
                <Typography
                  sx={{
                    textAlign: "center",
                    py: 2,
                    color: theme.palette.text.secondary,
                  }}
                >
                  {emptyText}
                </Typography>
              )}
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>

      {/* More filters*/}
      <Popover
        open={openMoreFilters}
        anchorEl={moreFiltersAnchorEl}
        onClose={() => setMoreFiltersAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableEnforceFocus
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              width: 340,
              mt: 1,
              border: "1px solid " + theme.palette.divider,
              boxShadow: "0px 4px 20px #00000022",
              borderRadius: 2,
            },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          pt={2}
          pb={1.5}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography fontWeight={600} fontSize="0.9rem">
              Mais filtros
            </Typography>
            {totalSelectedFilters > 0 && (
              <Box
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: "#fff",
                  borderRadius: "999px",
                  px: 0.8,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  lineHeight: 1.8,
                }}
              >
                {totalSelectedFilters}
              </Box>
            )}
          </Stack>
          {totalSelectedFilters > 0 && (
            <Button
              variant="text"
              size="small"
              sx={{ textTransform: "none", fontSize: "0.8rem" }}
              onClick={() => setSelectedFilters({})}
            >
              Limpar tudo
            </Button>
          )}
        </Stack>

        <Divider />

        <Stack gap={2.5} px={2} py={2}>
          {Object.entries(filters).map(([category, options]) => {
            const selected = selectedFilters[category] || [];
            return (
              <Box key={category}>
                <Stack direction="row" alignItems="center" gap={0.8} mb={0.75}>
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {category}
                  </Typography>
                  {selected.length > 0 && (
                    <Box
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: theme.palette.primary.main,
                        borderRadius: "999px",
                        px: 0.7,
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        lineHeight: 1.7,
                      }}
                    >
                      {selected.length}
                    </Box>
                  )}
                </Stack>

                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={options}
                  value={selected}
                  onChange={(_, newValue) =>
                    handleCategoryChange(category, newValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={selected.length === 0 ? "Selecionar..." : ""}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: (theme.palette as any).background
                            .paperLinkedMaps,
                          flexWrap: "wrap",
                          gap: 0.4,
                          py: "5px",
                          "& fieldset": { borderColor: theme.palette.divider },
                          "&:hover fieldset": {
                            borderColor: theme.palette.text.secondary,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                        "& .MuiAutocomplete-input": { minWidth: 60 },
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={option}
                          {...tagProps}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.main,
                            border:
                              "1px solid " +
                              alpha(theme.palette.primary.main, 0.3),
                            "& .MuiChip-deleteIcon": {
                              fontSize: "0.85rem",
                              color: alpha(theme.palette.primary.main, 0.5),
                              "&:hover": { color: theme.palette.error.main },
                            },
                          }}
                        />
                      );
                    })
                  }
                  renderOption={(props, option, { selected: isSelected }) => {
                    const { key, ...rest } = props as any;
                    return (
                      <Box key={key} component="li" {...rest}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          width="100%"
                        >
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color: isSelected
                                ? theme.palette.primary.main
                                : theme.palette.text.primary,
                              fontWeight: isSelected ? 600 : 400,
                            }}
                          >
                            {option}
                          </Typography>
                          {isSelected && (
                            <Done
                              sx={{
                                fontSize: "1rem",
                                color: theme.palette.primary.main,
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                    );
                  }}
                  slotProps={{
                    paper: {
                      elevation: 0,
                      sx: {
                        border: "1px solid " + theme.palette.divider,
                        boxShadow: "0px 4px 12px #00000018",
                        borderRadius: 1.5,
                        mt: 0.5,
                      },
                    },
                  }}
                  sx={{
                    "& .MuiAutocomplete-popupIndicator": { display: "none" },
                    "& .MuiAutocomplete-clearIndicator": { display: "none" },
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      </Popover>
    </>
  );
}
